import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { LinkItem, CampaignItem } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { QRCodeModal } from '../components/QRCodeModal';
import { Modal } from '../components/ui/Modal';
import {
  Search,
  Plus,
  Copy,
  ExternalLink,
  QrCode,
  Edit2,
  CopyPlus,
  Trash2,
  Archive,
  RefreshCw,
  SlidersHorizontal,
  Share2,
} from 'lucide-react';
import { copyToClipboard, formatDate, formatNumber } from '../lib/utils';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

interface LinksListProps {
  onNavigate: (path: string) => void;
  onOpenCreate: () => void;
  onEditLink: (id: string) => void;
}

export const LinksList: React.FC<LinksListProps> = ({ onNavigate, onOpenCreate, onEditLink }) => {
  const { showToast } = useToast();
  const { permissions } = useAuth();

  const [links, setLinks] = useState<LinkItem[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'total_clicks' | 'title'>('created_at');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modals state
  const [qrModalData, setQrModalData] = useState<{ shortCode: string; title: string } | null>(null);
  const [deleteConfirmLink, setDeleteConfirmLink] = useState<LinkItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15',
        sortBy,
        sortOrder: 'desc',
      });

      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (campaignFilter !== 'all') params.set('campaignId', campaignFilter);

      const data = await api.get<{
        items: LinkItem[];
        total: number;
        totalPages: number;
      }>(`/links?${params.toString()}`);

      setLinks(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (err: any) {
      showToast('Error loading links', err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, campaignFilter, sortBy, showToast]);

  const fetchCampaigns = async () => {
    try {
      const data = await api.get<{ items: CampaignItem[] }>('/campaigns');
      setCampaigns(data.items || []);
    } catch (err) {
      console.error('Failed to load campaigns for filter:', err);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleCopyLink = async (shortCode: string) => {
    const url = `${window.location.origin}/${shortCode}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      showToast('Copied to clipboard', url, 'success');
    }
  };

  const handleToggleStatus = async (link: LinkItem) => {
    const newStatus = link.status === 'active' ? 'disabled' : 'active';
    try {
      await api.patch(`/links/${link.id}/status`, { status: newStatus });
      setLinks((prev) =>
        prev.map((l) => (l.id === link.id ? { ...l, status: newStatus } : l))
      );
      showToast(`Link /${link.short_code} is now ${newStatus}`, '', 'info');
    } catch (err: any) {
      showToast('Failed to update status', err.message, 'error');
    }
  };

  const handleDuplicateLink = async (link: LinkItem) => {
    try {
      const result = await api.post<{ link: LinkItem }>(`/links/${link.id}/duplicate`);
      showToast('Link duplicated', `Created /${result.link.short_code}`, 'success');
      fetchLinks();
    } catch (err: any) {
      showToast('Duplicate failed', err.message, 'error');
    }
  };

  const handleDelete = async (isHard: boolean) => {
    if (!deleteConfirmLink) return;
    try {
      setIsDeleting(true);
      await api.delete(`/links/${deleteConfirmLink.id}?hard=${isHard}`);
      showToast(
        isHard ? 'Link permanently deleted' : 'Link archived',
        `/${deleteConfirmLink.short_code}`,
        'info'
      );
      setDeleteConfirmLink(null);
      fetchLinks();
    } catch (err: any) {
      showToast('Action failed', err.message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">
            Short Links Directory
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Total of {totalItems} marketing short URLs currently registered.
          </p>
        </div>

        <Button
          variant="primary"
          onClick={onOpenCreate}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Create Custom Short Link
        </Button>
      </div>

      {/* Filters and Search Bar */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Input
              placeholder="Search by title, shortcode, or URL..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              suffixIcon={<Search className="w-4 h-4 text-slate-500" />}
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
            >
              <option value="all">All Statuses (Active & Disabled)</option>
              <option value="active">Active Only</option>
              <option value="disabled">Disabled Only</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Campaign Filter */}
          <div>
            <select
              value={campaignFilter}
              onChange={(e) => {
                setCampaignFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
            >
              <option value="all">All Campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as any);
                setPage(1);
              }}
              className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
            >
              <option value="created_at">Sort: Newest First</option>
              <option value="total_clicks">Sort: Most Clicks</option>
              <option value="title">Sort: Alphabetical</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Links Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                <th className="py-3.5 px-4">Shortcode & Title</th>
                <th className="py-3.5 px-4">Destination Target</th>
                <th className="py-3.5 px-4">Campaign</th>
                <th className="py-3.5 px-4 text-center">Status</th>
                <th className="py-3.5 px-4 text-right">Clicks</th>
                <th className="py-3.5 px-4 text-right">Created</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-orange-400" />
                    Loading links from D1...
                  </td>
                </tr>
              ) : links.length > 0 ? (
                links.map((link) => (
                  <tr key={link.id} className="hover:bg-slate-900/40 transition-colors group">
                    {/* Shortcode & Title */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-orange-400 text-sm">
                          /{link.short_code}
                        </span>
                        <button
                          onClick={() => handleCopyLink(link.short_code)}
                          className="p-1 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors cursor-pointer"
                          title="Copy short URL"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-slate-200 font-medium truncate max-w-[220px] mt-0.5">
                        {link.title}
                      </p>
                      {link.metadata?.preview_mode === 'custom' && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-400/90 font-medium mt-1">
                          <Share2 className="w-2.5 h-2.5" /> Custom OG Preview
                        </span>
                      )}
                    </td>

                    {/* Destination URL */}
                    <td className="py-3.5 px-4">
                      <a
                        href={link.destination_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-200 flex items-center gap-1 font-mono truncate max-w-[240px] group/url"
                      >
                        <span className="truncate">{link.destination_url}</span>
                        <ExternalLink className="w-3 h-3 text-slate-500 group-hover/url:text-orange-400 shrink-0" />
                      </a>
                    </td>

                    {/* Campaign */}
                    <td className="py-3.5 px-4">
                      {link.campaign_name ? (
                        <span className="px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 font-medium truncate max-w-[140px] inline-block">
                          {link.campaign_name}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 text-center">
                      <button
                        onClick={() => handleToggleStatus(link)}
                        className="cursor-pointer"
                        title="Click to toggle status"
                      >
                        <Badge
                          variant={
                            link.status === 'active'
                              ? 'active'
                              : link.status === 'disabled'
                              ? 'disabled'
                              : 'archived'
                          }
                        >
                          {link.status}
                        </Badge>
                      </button>
                    </td>

                    {/* Clicks */}
                    <td className="py-3.5 px-4 text-right font-mono font-bold text-slate-100">
                      {formatNumber(link.total_clicks)}
                    </td>

                    {/* Created Date */}
                    <td className="py-3.5 px-4 text-right text-slate-400 font-mono text-[11px]">
                      {formatDate(link.created_at)}
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* QR Code Action */}
                        <button
                          onClick={() =>
                            setQrModalData({
                              shortCode: link.short_code,
                              title: link.title,
                            })
                          }
                          className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                          title="Generate QR code"
                        >
                          <QrCode className="w-3.5 h-3.5" />
                        </button>

                        {/* Edit Action */}
                        <button
                          onClick={() => onEditLink(link.id)}
                          className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                          title="Edit destination & metadata"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>

                        {/* Duplicate Action */}
                        <button
                          onClick={() => handleDuplicateLink(link)}
                          className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                          title="Duplicate link"
                        >
                          <CopyPlus className="w-3.5 h-3.5" />
                        </button>

                        {/* Delete / Archive Action */}
                        <button
                          onClick={() => setDeleteConfirmLink(link)}
                          className="p-1.5 rounded-lg bg-slate-800/80 hover:bg-rose-900/60 text-slate-400 hover:text-rose-300 transition-colors cursor-pointer"
                          title="Archive or delete link"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    No short links matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-800 text-xs">
            <span className="text-slate-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* QR Code Modal */}
      {qrModalData && (
        <QRCodeModal
          isOpen={!!qrModalData}
          onClose={() => setQrModalData(null)}
          shortCode={qrModalData.shortCode}
          linkTitle={qrModalData.title}
        />
      )}

      {/* Delete / Archive Confirmation Dialog */}
      {deleteConfirmLink && (
        <Modal
          isOpen={!!deleteConfirmLink}
          onClose={() => setDeleteConfirmLink(null)}
          title="Archive or Delete Link"
          description={`Target: /${deleteConfirmLink.short_code}`}
          maxWidth="md"
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-300 leading-relaxed">
              Archiving this link will immediately disable redirects with HTTP 410 Gone while preserving click analytics history and audit logs.
            </p>

            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs">
              <p className="font-semibold text-slate-200">{deleteConfirmLink.title}</p>
              <p className="text-slate-400 font-mono truncate mt-0.5">{deleteConfirmLink.destination_url}</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-2.5 pt-3 border-t border-slate-800">
              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmLink(null)}>
                Cancel
              </Button>

              <Button
                variant="secondary"
                size="sm"
                isLoading={isDeleting}
                onClick={() => handleDelete(false)}
                leftIcon={<Archive className="w-3.5 h-3.5 text-amber-400" />}
              >
                Archive Link (Soft)
              </Button>

              {permissions.canDeleteLinks && (
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={isDeleting}
                  onClick={() => handleDelete(true)}
                  leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                >
                  Hard Delete
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
